import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { useSession } from '@/store/useSession'
import PANASCheck from '@/components/PANASCheck/PANASCheck'
import styles from './SessionEndPage.module.css'

type EndStep = 'panas' | 'comparison'

type PostSessionDest = 'after' | 'notes'

function comparisonMessage(start: number | null, end: number): string {
  if (start === null) {
    return "You took a moment to notice. That's a gentle way to close."
  }
  const delta = end - start
  if (delta >= 10) return "Something shifted for the better. That's worth noticing."
  if (delta <= -10) return "It's been a lot. You showed up, and that matters."
  return "You're here, and you notice. That counts."
}

function parseDest(value: string | null): PostSessionDest {
  return value === 'notes' ? 'notes' : 'after'
}

export default function SessionEndPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { palette } = useTheme()
  const {
    clientPanasScore,
    clientPostPanasScore,
    postSessionPanasSkipped,
    setClientPostPanasScore,
    setPostSessionPanasSkipped,
  } = useSession()

  const dest = parseDest(searchParams.get('dest'))
  const [step, setStep] = useState<EndStep>(() =>
    clientPostPanasScore !== null ? 'comparison' : 'panas',
  )
  const [endScore, setEndScore] = useState<number | null>(clientPostPanasScore)

  useEffect(() => {
    if (postSessionPanasSkipped) {
      navigate(dest === 'notes' ? '/session/notes' : '/session/after', { replace: true })
    }
  }, [postSessionPanasSkipped, dest, navigate])

  const handlePanasComplete = (score: number) => {
    setPostSessionPanasSkipped(false)
    setClientPostPanasScore(score)
    setEndScore(score)
    setStep('comparison')
  }

  const handlePanasSkip = () => {
    setPostSessionPanasSkipped(true)
    navigate(dest === 'notes' ? '/session/notes' : '/session/after')
  }

  const paletteVars = {
    '--se-bg':   palette.bg,
    '--se-text': palette.text,
    '--se-ring': palette.ring,
  } as React.CSSProperties

  return (
    <main className={styles.page} style={paletteVars} aria-label="Session end">
      <div className={styles.contentCardWrap}>
        <div className={styles.contentCard}>
          {step === 'panas' && (
            <div className={styles.intro}>
              <header className={styles.header}>
                <h1 className={styles.heading}>Let's see where you are now.</h1>
                <p className={styles.subtitle}>
                  Same questions, just a moment to notice what's shifted.
                </p>
              </header>
              <PANASCheck onComplete={handlePanasComplete} onSkip={handlePanasSkip} />
            </div>
          )}

          {step === 'comparison' && endScore !== null && (
            <section className={styles.comparison} aria-live="polite">
              <p className={styles.comparisonMessage}>
                {comparisonMessage(clientPanasScore, endScore)}
              </p>

              <div className={styles.scorePair} aria-label="Start and end mood scores">
                <div className={styles.scoreBlock}>
                  <p className={styles.scoreLabel}>Start</p>
                  <p className={styles.scoreValue}>
                    {clientPanasScore !== null ? `${clientPanasScore}%` : '—'}
                  </p>
                </div>
                <div className={styles.scoreBlock}>
                  <p className={styles.scoreLabel}>Now</p>
                  <p className={styles.scoreValue}>{endScore}%</p>
                </div>
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => navigate('/session/after')}
                >
                  Your Space
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => navigate('/clients')}
                >
                  Done for today
                </button>
                {dest === 'notes' && (
                  <button
                    type="button"
                    className={styles.notesLink}
                    onClick={() => navigate('/session/notes')}
                  >
                    Practitioner notes →
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </main>
  )
}
